import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
    collection,
    collectionGroup,
    getDocs,
    onSnapshot,
    query,
    where,
} from "firebase/firestore";
import { markPaid } from "../lib/bookingSync";
import { formatCommission } from "../components/CommissionOverrideFields";

const rupees = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const toDate = (t) => t?.toDate?.() || null;

// A copy of a two-service booking written before the per-service split was
// stored: its providerAmount used only the primary commission on the full
// amount. Reassigning the booking recomputes it.
const missingSplit = (c) =>
    (c.secondaryCategoryId || c.secondaryCategoryName) && c.secondaryAmount == null;

// Per-provider payout totals, built from the providers' copies of completed
// bookings (ServiceProviders/{uid}/assignedBookings). "Mark paid" only touches
// the copies.
const Payouts = () => {
    const [copies, setCopies] = useState([]);
    const [names, setNames] = useState({}); // provider uid -> name
    const [expanded, setExpanded] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Loaded once, only to label the rows (copies don't carry the provider name).
    useEffect(() => {
        getDocs(collection(db, "ServiceProviders"))
            .then((snap) => {
                const map = {};
                snap.docs.forEach((d) => (map[d.id] = d.data().name));
                setNames(map);
            })
            .catch((err) => console.error("Error loading provider names:", err));
    }, []);

    useEffect(() => {
        const unsub = onSnapshot(
            query(
                collectionGroup(db, "assignedBookings"),
                where("status", "==", "completed")
            ),
            (snapshot) =>
                setCopies(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (err) => {
                console.error("Error loading payouts:", err);
                setError(
                    err?.code === "failed-precondition"
                        ? "Payouts need a Firestore index (assignedBookings.status, collection group) that isn't deployed in this project yet. Deploy firestore indexes, then reload."
                        : `Failed to load payouts: ${err?.message || "unknown error"}`
                );
            }
        );
        return unsub;
    }, []);

    // One row per provider: pending/paid totals and counts.
    const rows = useMemo(() => {
        const byProvider = {};
        copies.forEach((c) => {
            const row = (byProvider[c.providerId] ||= {
                providerId: c.providerId,
                pendingTotal: 0,
                paidTotal: 0,
                pendingCount: 0,
                paidCount: 0,
                needsReview: 0,
                bookings: [],
            });
            if (missingSplit(c)) row.needsReview += 1;
            const amount = Number(c.providerAmount) || 0;
            if (c.payoutStatus === "paid") {
                row.paidTotal += amount;
                row.paidCount += 1;
            } else {
                row.pendingTotal += amount;
                row.pendingCount += 1;
            }
            row.bookings.push(c);
        });
        // Bookings: unpaid first, then most recently completed.
        Object.values(byProvider).forEach((row) =>
            row.bookings.sort(
                (a, b) =>
                    (a.payoutStatus === "paid") - (b.payoutStatus === "paid") ||
                    (toDate(b.completedAt) || 0) - (toDate(a.completedAt) || 0)
            )
        );
        return Object.values(byProvider).sort(
            (a, b) => b.pendingTotal - a.pendingTotal
        );
    }, [copies]);

    const pay = async (list) => {
        if (list.length === 0) return;
        if (!window.confirm(`Mark ${list.length} booking(s) as paid?`)) return;
        setBusy(true);
        setError("");
        try {
            await markPaid(
                list.map((c) => ({ providerId: c.providerId, bookingId: c.bookingId }))
            );
        } catch (err) {
            console.error("Error marking paid:", err);
            setError(`Failed to mark paid: ${err?.message || "unknown error"}`);
        } finally {
            setBusy(false);
        }
    };

    const providerName = (row) => names[row.providerId] || row.providerId;
    const totals = rows.reduce(
        (t, r) => ({
            pending: t.pending + r.pendingTotal,
            paid: t.paid + r.paidTotal,
            pendingCount: t.pendingCount + r.pendingCount,
        }),
        { pending: 0, paid: 0, pendingCount: 0 }
    );

    return (
        <div className="h-screen overflow-y-auto p-4 bg-gray-50">
            <div className="max-w-7xl mx-auto w-full">
                <h1 className="text-3xl font-bold mb-6">Provider Payouts</h1>
                {error && <p className="text-red-600 mb-4">{error}</p>}

                <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="rounded bg-white p-4 shadow">
                        <p className="text-sm text-gray-500">Pending payout</p>
                        <p className="text-2xl font-semibold">{rupees(totals.pending)}</p>
                    </div>
                    <div className="rounded bg-white p-4 shadow">
                        <p className="text-sm text-gray-500">Unpaid bookings</p>
                        <p className="text-2xl font-semibold">{totals.pendingCount}</p>
                    </div>
                    <div className="rounded bg-white p-4 shadow">
                        <p className="text-sm text-gray-500">Paid so far</p>
                        <p className="text-2xl font-semibold">{rupees(totals.paid)}</p>
                    </div>
                </div>

                <div className="w-full border rounded shadow">
                    <table className="min-w-full border-collapse">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="border p-2">Provider</th>
                                <th className="border p-2">Pending (₹)</th>
                                <th className="border p-2">Pending #</th>
                                <th className="border p-2">Paid (₹)</th>
                                <th className="border p-2">Paid #</th>
                                <th className="border p-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center p-4">
                                        No completed bookings yet.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => {
                                    // Bulk pay skips amounts flagged for review.
                                    const pending = row.bookings.filter(
                                        (b) => b.payoutStatus !== "paid" && !missingSplit(b)
                                    );
                                    const open = expanded === row.providerId;
                                    return (
                                        <React.Fragment key={row.providerId}>
                                            <tr>
                                                <td className="border p-2">
                                                    {providerName(row)}
                                                    {row.needsReview > 0 && (
                                                        <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
                                                            {row.needsReview} to review
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="border p-2 text-right">{rupees(row.pendingTotal)}</td>
                                                <td className="border p-2 text-center">{row.pendingCount}</td>
                                                <td className="border p-2 text-right">{rupees(row.paidTotal)}</td>
                                                <td className="border p-2 text-center">{row.paidCount}</td>
                                                <td className="border p-2 space-x-2">
                                                    <button
                                                        onClick={() =>
                                                            setExpanded(open ? null : row.providerId)
                                                        }
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {open ? "Hide" : "Details"}
                                                    </button>
                                                    <button
                                                        disabled={busy || pending.length === 0}
                                                        onClick={() => pay(pending)}
                                                        className="text-green-700 hover:underline disabled:text-gray-400"
                                                    >
                                                        Mark all paid
                                                    </button>
                                                </td>
                                            </tr>
                                            {open && (
                                                <tr>
                                                    <td colSpan="6" className="border bg-gray-50 p-2">
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="text-left">
                                                                    <th className="p-1">Booking</th>
                                                                    <th className="p-1">Service</th>
                                                                    <th className="p-1">Completed</th>
                                                                    <th className="p-1 text-right">Payable</th>
                                                                    <th className="p-1 text-right">Company cut</th>
                                                                    <th className="p-1 text-right">Provider</th>
                                                                    <th className="p-1">Payout</th>
                                                                    <th className="p-1"></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {row.bookings.map((b) => {
                                                                    const custom = Object.values(
                                                                        b.commissionOverride || {}
                                                                    );
                                                                    const review = missingSplit(b);
                                                                    return (
                                                                        <tr
                                                                            key={b.id}
                                                                            className={`border-t align-top ${review ? "bg-red-50" : ""}`}
                                                                        >
                                                                            <td className="p-1">
                                                                                <div>{b.userName || "—"}</div>
                                                                                <div className="text-xs text-gray-500">
                                                                                    {b.bookingId}
                                                                                </div>
                                                                            </td>
                                                                            <td className="p-1">
                                                                                <div>
                                                                                    {b.categoryName}
                                                                                    {b.primaryAmount != null &&
                                                                                        ` · ${rupees(b.primaryAmount)}`}
                                                                                </div>
                                                                                {b.secondaryCategoryName && (
                                                                                    <div>
                                                                                        + {b.secondaryCategoryName}
                                                                                        {b.secondaryAmount != null &&
                                                                                            ` · ${rupees(b.secondaryAmount)}`}
                                                                                    </div>
                                                                                )}
                                                                                {custom.length > 0 && (
                                                                                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                                                                                        custom cut{" "}
                                                                                        {custom.map(formatCommission).join(" + ")}
                                                                                    </span>
                                                                                )}
                                                                                {review && (
                                                                                    <div className="text-xs text-red-700">
                                                                                        Amount ignores the secondary
                                                                                        service — reassign the booking to
                                                                                        recompute before paying.
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="p-1">
                                                                                {toDate(b.completedAt)?.toLocaleString() || "—"}
                                                                            </td>
                                                                            <td className="p-1 text-right">{rupees(b.payableAmount)}</td>
                                                                            <td className="p-1 text-right">
                                                                                {rupees(
                                                                                    (Number(b.payableAmount) || 0) -
                                                                                        (Number(b.providerAmount) || 0)
                                                                                )}
                                                                            </td>
                                                                            <td className="p-1 text-right font-medium">
                                                                                {rupees(b.providerAmount)}
                                                                            </td>
                                                                            <td className="p-1">
                                                                                {b.payoutStatus === "paid"
                                                                                    ? `paid ${toDate(b.paidAt)?.toLocaleDateString() || ""}`
                                                                                    : "pending"}
                                                                            </td>
                                                                            <td className="p-1">
                                                                                {b.payoutStatus !== "paid" && (
                                                                                    <button
                                                                                        disabled={busy}
                                                                                        onClick={() => pay([b])}
                                                                                        className="text-green-700 hover:underline disabled:text-gray-400"
                                                                                    >
                                                                                        Mark paid
                                                                                    </button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Payouts;
